import type { AppState } from '@app/App.store.js';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import axiosClient from '@ui-core/config/axiosClient';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { type Dispatch } from 'redux';
import { setUserData } from './userDataSlice';
import { getOriginalAuthorizationToken } from '@ui-core/utils/getOriginalAuthorizationToken.lib';
import { Empresa } from '@app/interfaces/RolesYEmpresas.interface';

export interface IDataCommitment {
    actualizarDatos: boolean;
    comentario: string;
    email: string;
    formajuridica: string;
    option: Array<string>;
    phone: string;
    grecaptchaToken?: string;
}

export interface ICommitmentDrawerSlice {
    error: boolean;
    errorMessage: string;
    isLoading: boolean;
    success: boolean;
}

export const initialState: ICommitmentDrawerSlice = {
    error: false,
    errorMessage: '',
    isLoading: false,
    success: false
};

export const getFormaJuridica = () => {
    const empresasAsignadasStorage = localStorage.getItem('empresasAsignadas');
    let formaJuridica = '';

    if (empresasAsignadasStorage) {
        const empresasAsignadas: Empresa[] = JSON.parse(
            empresasAsignadasStorage
        );
        const currentEnterprise = localStorage.getItem('rutEnterprise');

        for (const empresaAsignada of empresasAsignadas) {
            if (empresaAsignada.rutEmpresa === currentEnterprise)
                formaJuridica = empresaAsignada.formaJuridica;
        }
    }

    return formaJuridica;
};

export const commitmentDrawerSlice = createSlice({
    initialState,
    name: 'commitmentDrawer',
    reducers: {
        clearState: (state) => {
            state.error = false;
            state.errorMessage = '';
            state.success = false;
        },
        loading: (state) => {
            state.isLoading = true;
            state.error = false;
            state.errorMessage = '';
        },
        storeError: (state, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = true;
            state.errorMessage = action.payload;
        },
        storeSuccess: (state) => {
            state.isLoading = false;
            state.success = true;
        }
    }
});

export const useCommitmentDrawerSliceSelector = () =>
    useSelector<AppState, ICommitmentDrawerSlice>(
        ({ [commitmentDrawerSlice.name]: slice }) => slice
    );

export const { clearState, loading, storeError, storeSuccess } =
    commitmentDrawerSlice.actions;

export const storeCommitment =
    (data: IDataCommitment) =>
    async (dispatch: Dispatch): Promise<void> => {
        dispatch(loading());
        const axiosApi = axiosClient(
            `${import.meta.env.REACT_APP_HOME_API_URL}`
        );

        try {
            const rutEmpresa = localStorage.getItem('rutEnterprise');
            data.formajuridica = getFormaJuridica();
            const originalAuthorizationToken = getOriginalAuthorizationToken();

            if (!rutEmpresa) {
                dispatch(
                    storeError('No existe empresa para enviar el formulario')
                );
                return;
            }

            const body = { ...data, originalAuthorizationToken };

            const response = await axiosApi.post('v1/commitment', body, {
                headers: {
                    grecaptchatoken: data.grecaptchaToken!,
                    'ms-company-id': rutEmpresa
                }
            });

            if (data.actualizarDatos) {
                if (response.data.accessToken)
                    localStorage.setItem(
                        'Authorization',
                        `Bearer ${response.data.accessToken}`
                    );

                if (response.data.av1Token)
                    localStorage.setItem('AccessToken', response.data.av1Token);

                if (response.data.error) {
                    dispatch(storeError(response.data.error));
                } else {
                    dispatch(setUserData({ name: 'email', value: data.email }));
                    dispatch(
                        setUserData({
                            name: 'numeroTelefono',
                            value: data.phone.replaceAll(' ', '')
                        })
                    );
                }
            }

            dispatch(storeSuccess());
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                console.error(`Form commitment error:  ${error.message}`);
                dispatch(
                    storeError(
                        'Algo ha ocurrido mal. Vuelve a intentarlo más tarde'
                    )
                );
            }
        }
    };

export default commitmentDrawerSlice.reducer;
